import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

/**
 * Deals page body — mirrors the canonical Weidner "Deals" /
 * cortfurniturerental custom page (e.g. Quail Landing):
 *   1. Partner intro mentioning the property + CORT discount codes
 *   2. Tall CORT flyer image with the Weidner promo codes
 *   3. CTA out to CORT
 *
 * The page hero (HeroSolidBG) supplies the "CORT Furniture Rental"
 * title; this section is the content below it.
 */

const flyerImageDef: SlotGroupDefinition = {
  label: 'CORT Flyer Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://resource.rentcafe.com/image/upload/x_0,y_0,w_1100,h_1700,c_crop/q_auto,f_auto,c_limit,w_1100/s3/2/111265/weidner-8-5-x-5-5-flyer-1(32).png',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue:
        'CORT furniture rental flyer with exclusive Weidner discount codes for furniture rentals and purchases',
    },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CORT CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: 'https://www.cort.com/',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Furnish Your Home with CORT',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

export function Deals() {
  const flyer = useSlotGroup('deals.flyer_image', flyerImageDef);
  const ctaLink = useSlotGroup('deals.cta_link', ctaLinkDef);

  useRevealStyles();
  const introRef = useScrollReveal<HTMLDivElement>();
  const flyerRef = useScrollReveal<HTMLDivElement>();
  const ctaRef = useScrollReveal<HTMLDivElement>();

  const flyerHref = ctaLink.url || 'https://www.cort.com/';
  const flyerTarget = ctaLink._target || '_blank';

  return (
    <section className="deals-section bg-[var(--vibe-color-background)] py-10 md:py-16">
      <div className="deals-inner mx-auto px-3 sm:px-4 lg:px-6">
        <div ref={introRef} className="reveal-fade-up deals-intro text-center mb-8 md:mb-10">
          <RichTextContent
            path="deals.intro"
            label="Intro"
            as="div"
            className="font-body text-base md:text-lg leading-relaxed text-[var(--vibe-color-text)] [&_p]:mb-0"
          />
        </div>

        {flyer.image && (
          <div ref={flyerRef} className="reveal-fade-up deals-flyer mb-8 md:mb-10">
            <EditableSlotGroup groupPath="deals.flyer_image" definition={flyerImageDef}>
              <a
                href={flyerHref}
                target={flyerTarget}
                rel={flyerTarget === '_blank' ? 'noopener noreferrer' : undefined}
                className="deals-flyer-link block mx-auto"
                aria-label={ctaLink.label || 'View CORT furniture rental offers'}
              >
                <VibesitesImage
                  url={flyer.image}
                  alt={flyer.alt || ''}
                  className="w-full h-auto"
                  sizes="(max-width: 768px) 100vw, 90vw"
                />
              </a>
            </EditableSlotGroup>
          </div>
        )}

        <div ref={ctaRef} className="reveal-fade-up flex justify-center">
          <EditableSlotGroup groupPath="deals.cta_link" definition={ctaLinkDef}>
            <a
              href={ctaLink.url || 'https://www.cort.com/'}
              target={ctaLink._target || '_blank'}
              rel={
                (ctaLink._target || '_blank') === '_blank'
                  ? 'noopener noreferrer'
                  : undefined
              }
              className="deals-cta"
            >
              {ctaLink.label || 'Furnish Your Home with CORT'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .deals-inner {
          max-width: min(90rem, 96vw);
        }
        .deals-intro {
          max-width: 42rem;
          margin-left: auto;
          margin-right: auto;
        }
        .deals-flyer-link {
          width: 100%;
          max-width: min(72rem, 100%);
          transition: transform 0.35s ease, box-shadow 0.35s ease;
        }
        .deals-flyer-link:hover {
          transform: translateY(-4px);
        }
        .deals-flyer-link:focus-visible {
          outline: 2px solid var(--vibe-color-primary);
          outline-offset: 4px;
        }
        .deals-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 1rem 2.25rem;
          font-family: var(--vibe-font-heading);
          font-size: 0.95rem;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          text-decoration: none;
          color: var(--vibe-color-text-on-primary, #ffffff);
          background: var(--vibe-color-primary);
          border: 2px solid var(--vibe-color-primary);
          transition: background 0.25s ease, color 0.25s ease, transform 0.25s ease;
        }
        .deals-cta:hover {
          background: color-mix(in srgb, var(--vibe-color-primary) 85%, #ffffff);
          border-color: color-mix(in srgb, var(--vibe-color-primary) 85%, #ffffff);
          color: var(--vibe-color-text-on-primary, #ffffff);
          transform: translateY(-2px);
        }
        .deals-cta:focus-visible {
          outline: 2px solid var(--vibe-color-primary);
          outline-offset: 3px;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Deals',
  displayName: 'Deals',
  tokens: {
    'deals.intro': {
      label: 'Intro',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_name"></span> has partnered with CORT to bring you furniture rentals at a discounted price! Save on furniture when you use the Weidner codes shown below!</p>',
    },
  },
  slotGroups: {
    'deals.flyer_image': flyerImageDef,
    'deals.cta_link': ctaLinkDef,
  },
};

(Deals as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Deals;
